import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../../atoms/Spinner/Spinner';
import ReportingDetailsVerificationStatusesEditTemplate from '../../templates/ReportingDetailsVerificationStatusesEditTemplate/ReportingDetailsVerificationStatusesEditTemplate';
import { showError, showSuccess } from '../../../utils/Notifications';
import { VerificationStatusResponseDto } from '../../../store/landscapeAPI/verificationStatus/dto/response/verification-status-response.dto';
import {
  fetchVerificationStatusById,
  updateVerificationStatus,
} from '../../../store/landscapeAPI/verificationStatus/reducers/verification-status.slice';
import { VerificationStatusUpdateRequestDto } from '../../../store/landscapeAPI/verificationStatus/dto/request/verification-status-update-request.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';

interface ReportingDetailsVerificationStatusesEditPageProps {}

const ReportingDetailsVerificationStatusesEditPage: FC<
  ReportingDetailsVerificationStatusesEditPageProps
> = () => {
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatusResponseDto>();
  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const { id } = useParams();

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const handleOnChange = (
    verificationStatus: VerificationStatusResponseDto
  ) => {
    setVerificationStatus(verificationStatus);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const { status, error } = useSelector(
    (state: RootState) => state.verificationStatusSlice
  );

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  useEffect(() => {
    if (id) {
      dispatch(
        fetchVerificationStatusById({ id: id, language: selectedLanguage })
      )
        .unwrap()
        .then((response) => setVerificationStatus(response));
    }
  }, [dispatch, id, selectedLanguage]);

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!verificationStatus) return;
    const params: TranslateParams<VerificationStatusResponseDto> = {
      obj: verificationStatus,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as VerificationStatusResponseDto;

    const updateRequest: VerificationStatusUpdateRequestDto = {
      name: translatedResponse.name,
      description: translatedResponse.description,
      logoUrl: verificationStatus.logoUrl,
      id: verificationStatus.id,
      published: verificationStatus.published,
    };

    await dispatch(
      updateVerificationStatus({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const handleEditVerificationStatus = async (
    verificationStatus: VerificationStatusResponseDto
  ) => {
    const logoUrl = await processMedia(
      verificationStatus.logoUrl ?? '',
      imageMeta
    );

    const data: VerificationStatusUpdateRequestDto = {
      logoUrl: logoUrl.href,
      description: verificationStatus.description || '',
      id: verificationStatus.id,
      name: verificationStatus.name,
      published: verificationStatus.published,
    };

    await dispatch(
      updateVerificationStatus({
        updateRequest: data,
        language: selectedLanguage,
      })
    );

    showSuccess('Verification status saved successfully.');
    navigate('/reporting-details/verification-statuses');
  };

  if (
    status === 'loading' ||
    mediaStatus === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  if (status === 'failed' || mediaStatus === 'failed') {
    showError(error || 'Verification Status update failed!');
  }

  return (
    <>
      {verificationStatus && (
        <ReportingDetailsVerificationStatusesEditTemplate
          data={verificationStatus}
          handleEditVerificationStatus={handleEditVerificationStatus}
          handleOnChange={handleOnChange}
          handleImageOnChange={handleImageOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </>
  );
};

export default ReportingDetailsVerificationStatusesEditPage;
